import { create } from 'zustand';
import type { LoginResult, SessionUser } from './types';

/**
 * Access токенът стои САМО в паметта — презареждането на страницата го губи и
 * се вади нов с refresh токена. Refresh токенът е в localStorage, защото
 * диспечерът затваря браузъра в 18:00 и отваря пак в 07:00, а входът с MFA
 * два пъти на ден е повод хората да си запишат кода на монитора.
 */
export const REFRESH_KEY = 'kmdx.dispatch.refresh';
const USER_KEY = 'kmdx.dispatch.user';

function readStored<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeStored(key: string, value: unknown): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* режим „без съхранение" — сесията ще живее само в раздела */
  }
}

interface SessionState {
  accessToken: string | null;
  accessExpiresAt: number | null;
  refreshToken: string | null;
  user: SessionUser | null;
  /** Докато се опитваме да възстановим сесия от refresh токена */
  bootstrapping: boolean;
  setSession: (result: LoginResult) => void;
  setTokens: (tokens: { accessToken: string; expiresIn: number; refreshToken: string }) => void;
  setUser: (user: SessionUser) => void;
  clear: () => void;
  finishBootstrap: () => void;
}

export const useSession = create<SessionState>((set) => ({
  accessToken: null,
  accessExpiresAt: null,
  refreshToken: readStored<string>(REFRESH_KEY),
  user: readStored<SessionUser>(USER_KEY),
  bootstrapping: readStored<string>(REFRESH_KEY) !== null,

  setSession: (result) => {
    writeStored(REFRESH_KEY, result.refreshToken);
    writeStored(USER_KEY, result.user);
    set({
      accessToken: result.accessToken,
      accessExpiresAt: Date.now() + result.expiresIn * 1000,
      refreshToken: result.refreshToken,
      user: result.user,
      bootstrapping: false,
    });
  },

  setTokens: (tokens) => {
    writeStored(REFRESH_KEY, tokens.refreshToken);
    set({
      accessToken: tokens.accessToken,
      accessExpiresAt: Date.now() + tokens.expiresIn * 1000,
      refreshToken: tokens.refreshToken,
      bootstrapping: false,
    });
  },

  setUser: (user) => {
    writeStored(USER_KEY, user);
    set({ user });
  },

  clear: () => {
    writeStored(REFRESH_KEY, null);
    writeStored(USER_KEY, null);
    set({
      accessToken: null,
      accessExpiresAt: null,
      refreshToken: null,
      user: null,
      bootstrapping: false,
    });
  },

  finishBootstrap: () => set({ bootstrapping: false }),
}));

export const sessionSnapshot = () => useSession.getState();
